import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";

/* ────────────────────────────────────────────────────────────
 * showgirl desire quiz — "who's driving your desire?"
 * standalone public page. isolated styles. no auth required.
 * palette: black #0a0a0a · gold #c9a84c · cream #f5f0e8
 * ──────────────────────────────────────────────────────────── */

const COLORS = { bg: "#0a0a0a", gold: "#c9a84c", cream: "#f5f0e8" };

const QUESTIONS = [
  {
    q: "you have a free evening. nothing planned. what happens?",
    a: [
      { k: "a", t: "i catch up on emails and meal prep like the responsible adult i was raised to be." },
      { k: "b", t: "i scroll until my thumb cramps, then feel guilty for wasting the night." },
      { k: "c", t: "i light a candle, put on a slow jam and let the night decide." },
    ],
  },
  {
    q: "someone compliments your body. you:",
    a: [
      { k: "a", t: "say \"thanks!\" then change the subject so fast it gets whiplash." },
      { k: "b", t: "squint at them. what do they want? nobody's that nice for free." },
      { k: "c", t: "let it soak in like shea butter. then say thank you, because they're right." },
    ],
  },
  {
    q: "you own a red lipstick. when does she come out?",
    a: [
      { k: "a", t: "weddings, galas and the occasional work dinner. she keeps a strict schedule." },
      { k: "b", t: "she's been in a drawer since 2019, waiting on a special occasion that never rsvp'd." },
      { k: "c", t: "anytime the mood strikes me, and the mood strikes me pretty darn often. i look cute!" },
    ],
  },
  {
    q: "your song comes on in the grocery store. you:",
    a: [
      { k: "a", t: "nod once, respectfully, and stick to the list. the list is the list." },
      { k: "b", t: "hum along until someone rolls their cart into my aisle." },
      { k: "c", t: "drop it low by the avocados. they've seen worse." },
    ],
  },
  {
    q: "a big feeling hits out of nowhere. grief, longing, rage, the works. you:",
    a: [
      { k: "a", t: "\"i'm fine.\" next item on the agenda, please and thank you." },
      { k: "b", t: "replay it at 2am like a bad rerun and wonder what's wrong with me." },
      { k: "c", t: "let it move through me. if i cry in the car, the car can handle it." },
    ],
  },
  {
    q: "you catch your reflection while getting dressed. first thought?",
    a: [
      { k: "a", t: "everything's tucked and lint-rolled. let's go." },
      { k: "b", t: "a full inspection, like i'm airport security and my stomach is a suspicious bag." },
      { k: "c", t: "i wink. sometimes twice." },
    ],
  },
  {
    q: "a friend texts: \"spontaneous plans tonight?\" you:",
    a: [
      { k: "a", t: "check my calendar, then my other calendar." },
      { k: "b", t: "think of all the reasons i should say no, then pick the one least likely to get an eye roll." },
      { k: "c", t: "\"send the address. what are we wearing?\"" },
    ],
  },
  {
    q: "it's your rest day. how does rest feel?",
    a: [
      { k: "a", t: "like something i have to earn, preferably with a finished to-do list." },
      { k: "b", t: "like i'm getting away with a crime." },
      { k: "c", t: "delicious. i'm on vacation, baby. do not call me." },
    ],
  },
];

const RESULTS = {
  organizer: {
    key: "organizer",
    name: "the organizer",
    description: [
      "she runs your life like an air traffic controller on a triple espresso. calendars, labels, a five-year plan with color-coded tabs. she's the reason the bills get paid and the birthday cards arrive on time. give her a round of applause, then give her a nap.",
      "when the organizer drives, desire turns into a task. you pencil it in between the dentist and the dry cleaning, and it ends up about as sexy as filing taxes. nothing's wrong with you, sugar. you just hired a project manager to do a lover's job, and she's been working doubles.",
    ],
    try_tonight:
      "before your phone gets a single second of you tomorrow, spend two minutes touching textures, like the sheets, the pillowcase, or the soft skin inside your arm. in showgirl desire, it's called the glove, and it's how we give the organizer her first coffee break in years.",
  },
  protector: {
    key: "protector",
    name: "the protector",
    description: [
      "she's the bouncer at the club of your life, and she's been checking ids since you were twelve. \"is that skirt too short? is that laugh too loud? what will the church ladies say?\" she learned early what happened to women who wanted things out loud, and she's kept you out of trouble ever since. bless her heart, she's exhausted.",
      "when the protector drives, wanting feels dangerous, so you turn your own volume down before anyone can ask. she means well, but she's guarded the door so long she forgot there's a party inside. and baby, it's your party.",
    ],
    try_tonight:
      "stand in front of a mirror and set a timer for three minutes. look at yourself the way you'd look at a sunset over the ocean, with no fixing, no inventory and no sucking it in. in showgirl desire, it's called the mirror appointment, and it teaches the protector that being seen can be safe.",
  },
  "disrespectful-woman": {
    key: "disrespectful-woman",
    name: "the disrespectful woman",
    description: [
      "she dances before checking whether the room approves. she wears red lipstick whenever the mood strikes. she sprays the blessed perfume, sniffs her own wrist and whispers, \"god, i smell incredible.\" she treats your body like a playground, and she has no plans to go home before the streetlights come on.",
      "she's awake in you, and that's rare. for a lot of us, she went quiet the day life got optimized. your job now is to keep her fed, because the organizer and the protector will try to snatch the wheel back the minute life gets busy.",
    ],
    try_tonight:
      "make a three-song soundtrack of songs that move your hips before your brain has an opinion. play it while you get ready tomorrow, and drop it low at least once.",
  },
};

const CHARACTERS = [
  {
    n: "character 1",
    title: "the organizer",
    body: "she owns the label maker, the five-year plan and every password you've ever forgotten. she gets it all done, and she's tired of doing it alone.",
  },
  {
    n: "character 2",
    title: "the protector",
    body: "she's the bouncer of your life. \"is this safe? is this too much? what will people think?\" she remembers every time wanting something got you in trouble, and she's not about to let it happen again.",
  },
  {
    n: "character 3",
    title: "the disrespectful woman",
    body: "she drops it low by the avocados and wears red lipstick whenever the mood strikes. she's playful, sensual and curious, and for a lot of us, she's been locked out of the house for years.",
  },
  {
    n: "character 4",
    title: "the oracle",
    body: "she's the deep exhale. she's calm, grateful and connected to something bigger than your inbox. she's always home, but you have to knock. a slow breath, a walk outside or a quiet thank-you will get you in the door. she's also the one who can hold the protector's hand when she starts to panic.",
  },
];

function scoreAnswers(answers) {
  const counts = { a: 0, b: 0, c: 0 };
  answers.forEach((v) => { if (v && counts[v] !== undefined) counts[v] += 1; });
  const max = Math.max(counts.a, counts.b, counts.c);
  // ties: include organizer or protector → show that one.
  // if both organizer AND protector tied together → protector.
  const atMax = Object.keys(counts).filter((k) => counts[k] === max);
  if (atMax.length === 1) {
    return { a: "organizer", b: "protector", c: "disrespectful-woman" }[atMax[0]];
  }
  if (atMax.includes("b")) return "protector";
  if (atMax.includes("a")) return "organizer";
  return "disrespectful-woman";
}

function useHeadTags() {
  // Inject OG + title tags so link previews on IG/FB/Twitter look right.
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "who's driving your desire? | showgirl desire";
    const tags = [
      { name: "description", content: "a 2-minute quiz to find out which character in your brain has been running your desire, plus a ritual to try tonight." },
      { property: "og:title", content: "who's driving your desire? | showgirl desire" },
      { property: "og:description", content: "a 2-minute quiz to find out which character in your brain has been running your desire, plus a ritual to try tonight." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: typeof window !== "undefined" ? window.location.href : "" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "who's driving your desire? | showgirl desire" },
      { name: "twitter:description", content: "a 2-minute quiz to find out which character in your brain has been running your desire, plus a ritual to try tonight." },
    ];
    const created = tags.map(({ name, property, content }) => {
      const sel = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
      let el = document.head.querySelector(sel);
      let wasCreated = false;
      if (!el) {
        el = document.createElement("meta");
        if (name) el.setAttribute("name", name);
        if (property) el.setAttribute("property", property);
        document.head.appendChild(el);
        wasCreated = true;
      }
      const prev = el.getAttribute("content");
      el.setAttribute("content", content);
      return { el, prev, wasCreated };
    });
    return () => {
      document.title = prevTitle;
      created.forEach(({ el, prev, wasCreated }) => {
        if (wasCreated) el.remove();
        else if (prev !== null) el.setAttribute("content", prev);
      });
    };
  }, []);
}

/* ────────── small primitives ────────── */
const Btn = ({ children, onClick, disabled, "data-testid": tid, variant = "primary" }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    data-testid={tid}
    style={{
      background: variant === "primary" ? COLORS.gold : "transparent",
      color: variant === "primary" ? COLORS.bg : COLORS.cream,
      border: variant === "primary" ? "none" : `1px solid ${COLORS.gold}`,
      padding: "16px 32px",
      fontSize: "14px",
      letterSpacing: "1.5px",
      textTransform: "lowercase",
      fontFamily: "inherit",
      cursor: disabled ? "not-allowed" : "pointer",
      borderRadius: "2px",
      transition: "transform 0.15s ease, opacity 0.15s ease",
      opacity: disabled ? 0.5 : 1,
    }}
    onMouseOver={(e) => !disabled && (e.currentTarget.style.transform = "translateY(-1px)")}
    onMouseOut={(e) => (e.currentTarget.style.transform = "translateY(0)")}
  >
    {children}
  </button>
);

const Eyebrow = ({ children }) => (
  <div style={{
    fontSize: "11px",
    letterSpacing: "4px",
    textTransform: "uppercase",
    color: COLORS.gold,
    marginBottom: "20px",
    fontFamily: "'Inter', sans-serif",
  }}>{children}</div>
);

/* ────────── share image generator ────────── */
async function makeShareImage(resultName) {
  const w = 1080, h = 1920;
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d");

  // Background
  ctx.fillStyle = COLORS.bg;
  ctx.fillRect(0, 0, w, h);

  // Subtle gold corner marks
  ctx.strokeStyle = COLORS.gold;
  ctx.lineWidth = 2;
  const inset = 80, len = 100;
  [[inset,inset],[w-inset,inset],[inset,h-inset],[w-inset,h-inset]].forEach(([x,y],i)=>{
    const dx = i%2===0?1:-1, dy = i<2?1:-1;
    ctx.beginPath();
    ctx.moveTo(x, y+dy*len); ctx.lineTo(x, y); ctx.lineTo(x+dx*len, y);
    ctx.stroke();
  });

  // Eyebrow label
  ctx.fillStyle = COLORS.gold;
  ctx.font = "500 32px 'Inter', sans-serif";
  ctx.textAlign = "center";
  ctx.fillText("SHOWGIRL DESIRE · QUIZ", w/2, 240);

  // Main text — wrapped
  ctx.fillStyle = COLORS.cream;
  ctx.font = "italic 300 68px Georgia, serif";
  const lines = [
    "turns out",
    resultName,
    "has been driving",
    "my desire.",
    "",
    "who's driving yours?",
  ];
  let y = 700;
  lines.forEach((line) => {
    ctx.fillText(line, w/2, y);
    y += 110;
  });

  // Footer URL
  ctx.font = "500 36px 'Inter', sans-serif";
  ctx.fillStyle = COLORS.gold;
  ctx.fillText("quiz.showgirldesire.com", w/2, h - 220);

  return await new Promise((resolve) => canvas.toBlob(resolve, "image/png", 0.95));
}

/* ────────── main page ────────── */
export default function Quiz() {
  useHeadTags();
  const [stage, setStage] = useState("landing"); // landing | quiz | email | result
  const [qIdx, setQIdx] = useState(0);
  const [answers, setAnswers] = useState(Array(QUESTIONS.length).fill(null));
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [submitting, setSubmitting] = useState(false);
  const [resultKey, setResultKey] = useState(null);
  const quizRef = useRef(null);

  const scrollToQuiz = () => {
    setStage("quiz");
    setQIdx(0);
    setTimeout(() => quizRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  const chooseAnswer = (letter) => {
    const next = [...answers];
    next[qIdx] = letter;
    setAnswers(next);
    if (qIdx < QUESTIONS.length - 1) {
      setQIdx(qIdx + 1);
    } else {
      setStage("email");
    }
  };

  const stepBack = () => {
    if (qIdx > 0) setQIdx(qIdx - 1);
    else setStage("landing");
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    const computed = scoreAnswers(answers);
    setResultKey(computed);
    setSubmitting(true);
    try {
      await api.post("/quiz/submit", {
        email: email.trim(),
        result: computed,
        answers,
        website,
      });
    } catch (err) {
      // Never block the result — if email send fails, we still show the result.
      // Only hard-block on 400s like honeypot or 429s.
      const status = err?.response?.status;
      if (status === 400) {
        toast.error(err?.response?.data?.detail || "please check that email");
        setSubmitting(false);
        return;
      }
      if (status === 429) {
        toast.error(err?.response?.data?.detail || "too many submissions. try again in a bit.");
        setSubmitting(false);
        return;
      }
    } finally {
      setSubmitting(false);
    }
    setStage("result");
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
  };

  const retake = () => {
    setStage("landing");
    setQIdx(0);
    setAnswers(Array(QUESTIONS.length).fill(null));
    setEmail("");
    setResultKey(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const share = async () => {
    if (!resultKey) return;
    const result = RESULTS[resultKey];
    const text = `turns out ${result.name} has been driving my desire. who's driving yours?\n\nquiz.showgirldesire.com`;
    try {
      const blob = await makeShareImage(result.name);
      const file = new File([blob], "whos-driving-your-desire.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text });
        return;
      }
      // Fallback — download the image
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "whos-driving-your-desire.png";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("image saved. share it anywhere.");
    } catch (err) {
      toast.error("could not create share image");
    }
  };

  const progressPct = useMemo(() => {
    if (stage === "quiz") return ((qIdx + 1) / QUESTIONS.length) * 100;
    if (stage === "email") return 100;
    return 0;
  }, [stage, qIdx]);

  return (
    <div
      style={{
        background: COLORS.bg,
        color: COLORS.cream,
        minHeight: "100vh",
        fontFamily: "Georgia, serif",
        margin: 0,
      }}
      data-testid="quiz-page"
    >
      {/* progress bar — visible during quiz/email */}
      {(stage === "quiz" || stage === "email") && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, height: "2px",
          background: "rgba(201,168,76,0.2)", zIndex: 40,
        }}>
          <div style={{
            height: "100%", width: `${progressPct}%`,
            background: COLORS.gold, transition: "width 0.3s ease",
          }} />
        </div>
      )}

      {stage === "landing" && (
        <LandingSections onStart={scrollToQuiz} />
      )}

      {stage === "quiz" && (
        <div ref={quizRef}>
          <QuizView
            question={QUESTIONS[qIdx]}
            index={qIdx}
            total={QUESTIONS.length}
            onPick={chooseAnswer}
            onBack={stepBack}
          />
        </div>
      )}

      {stage === "email" && (
        <EmailView
          email={email}
          setEmail={setEmail}
          website={website}
          setWebsite={setWebsite}
          onSubmit={submitEmail}
          submitting={submitting}
          onBack={() => setStage("quiz")}
        />
      )}

      {stage === "result" && resultKey && (
        <ResultView
          result={RESULTS[resultKey]}
          chapterUrl="/course/chapter-02?preview=1"
          onShare={share}
          onRetake={retake}
        />
      )}
    </div>
  );
}

/* ────────── landing sections ────────── */
function LandingSections({ onStart }) {
  return (
    <div style={{ padding: "0 24px" }}>
      {/* hero */}
      <section style={sectionStyle(100, 60)}>
        <div style={{ maxWidth: 760, margin: "0 auto", textAlign: "center" }}>
          <Eyebrow>who's driving your desire?</Eyebrow>
          <h1 style={heroH1}>who's driving your desire?</h1>
          <p style={{ ...bodyText, maxWidth: 620, margin: "28px auto 0" }}>
            there are four characters living in your brain, and one of them has been hogging the steering wheel for years. if you've been feeling more tired spreadsheet than sultry sundress lately, she's probably why.
          </p>
          <p style={{ ...bodyText, maxWidth: 620, margin: "16px auto 36px" }}>
            take the 2-minute quiz to find out who's driving, and how to get your keys back.
          </p>
          <Btn onClick={onStart} data-testid="hero-start-btn">find out who's driving</Btn>
        </div>
      </section>

      {/* sound familiar */}
      <section style={sectionStyle(60, 60)}>
        <div style={{ maxWidth: 680, margin: "0 auto" }}>
          <h2 style={h2Style}>sound familiar?</h2>
          <ul style={ulStyle}>
            <li style={liStyle}>you built the life. the career, the home, the calendar that runs like a swiss watch. so why does it all feel a little beige?</li>
            <li style={liStyle}>your red lipstick has been in that drawer so long she should be paying rent.</li>
            <li style={liStyle}>desire feels like one more thing on the to-do list, somewhere under "call the dentist."</li>
            <li style={liStyle}>you remember a version of you who danced in the kitchen for no reason. you miss her. you're not sure where she went.</li>
          </ul>
          <p style={{ ...bodyText, fontStyle: "italic", marginTop: 32 }}>
            if you nodded at even one of these, this quiz was made for you.
          </p>
        </div>
      </section>

      {/* walk away with */}
      <section style={sectionStyle(60, 60)}>
        <div style={{ maxWidth: 680, margin: "0 auto" }}>
          <h2 style={h2Style}>what you'll walk away with</h2>
          <p style={bodyText}>in about two minutes, you'll know:</p>
          <ul style={{ ...ulStyle, marginTop: 24 }}>
            <li style={liStyle}><strong style={strongStyle}>who's been driving.</strong> you'll get the name of the character running your show, and why she grabbed the wheel in the first place. spoiler: she thought she was helping.</li>
            <li style={liStyle}><strong style={strongStyle}>why you've been feeling flat.</strong> finally, an answer that doesn't involve "just relax" or "have you tried date night?"</li>
            <li style={liStyle}><strong style={strongStyle}>what to do about it tonight.</strong> you'll get one small ritual from showgirl desire to try before bed. no candles required. okay, candles encouraged.</li>
            <li style={liStyle}><strong style={strongStyle}>a free chapter in your inbox.</strong> the respectability bargain is the chapter that names the deal you made with your desire, and who set the price.</li>
          </ul>
        </div>
      </section>

      {/* four characters */}
      <section style={sectionStyle(60, 60)}>
        <div style={{ maxWidth: 760, margin: "0 auto" }}>
          <h2 style={h2Style}>meet the four characters</h2>
          <p style={bodyText}>
            jill bolte taylor is a neuroanatomist who had a stroke on the left side of her brain and spent eight years finding her way back. along the way, she mapped four characters living inside every one of us. they've got cells, circuits and their own zip code. think of them as the four people riding in your car.
          </p>
          <div style={{ display: "grid", gap: 20, marginTop: 36 }}>
            {CHARACTERS.map((c) => (
              <div key={c.n} style={cardStyle} data-testid={`char-card-${c.n.replace(" ", "-")}`}>
                <div style={{ fontSize: 11, letterSpacing: 3, textTransform: "uppercase", color: COLORS.gold, marginBottom: 8 }}>{c.n}</div>
                <div style={{ fontFamily: "Georgia, serif", fontSize: 24, fontStyle: "italic", color: COLORS.cream, marginBottom: 12 }}>{c.title}.</div>
                <div style={{ ...bodyText, fontSize: 15 }}>{c.body}</div>
              </div>
            ))}
          </div>
          <p style={{ ...bodyText, marginTop: 32, fontSize: 15 }}>
            the quiz tells you which of the first three has been driving, since those are the three we get up close with in showgirl desire.
          </p>
          <p style={{ fontSize: 12, color: "rgba(245,240,232,0.5)", fontStyle: "italic", marginTop: 16 }}>
            based on whole brain living by jill bolte taylor (2021).
          </p>
        </div>
      </section>

      {/* so what's showgirl desire */}
      <section style={sectionStyle(60, 100)}>
        <div style={{ maxWidth: 680, margin: "0 auto" }}>
          <h2 style={h2Style}>so, what's showgirl desire?</h2>
          <p style={bodyText}>
            back in the day, i'd shake so hard on stage my armpits itched. now i peel off gloves for a living, slowly and on purpose, and i love every second of being seen. people call showgirls brave all the time. i think the better word is irreverent. we stopped asking permission to enjoy our own bodies, and irreverence is a skill you can learn.
          </p>
          <p style={{ ...bodyText, marginTop: 20 }}>
            showgirl desire is the self-guided workshop that teaches it. it's for the woman who built the life and misplaced her spark along the way.
          </p>
          <p style={{ ...bodyText, marginTop: 20 }}>inside, you'll find:</p>
          <ul style={{ ...ulStyle, marginTop: 16 }}>
            <li style={liStyle}>an intro, 10 chapters and a conclusion weaving together burlesque tradition, feminist history and the science of wanting</li>
            <li style={liStyle}>a practice in every chapter, so the ideas land in your body and not only in your head</li>
            <li style={liStyle}>a 30-night evidence journal for collecting proof that she's coming back</li>
            <li style={liStyle}>little luxuries in the workshop app, like dark mode, a font toggle and a way to save your favorite lines, so you can read in bed at your own pace</li>
          </ul>
          <p style={{ ...bodyText, marginTop: 24 }}>
            your desire isn't broken. the conditions are just wrong. let's fix the conditions.
          </p>
          <p style={{ ...bodyText, marginTop: 20, fontStyle: "italic" }}>
            but first, start the quiz.
          </p>
          <div style={{ marginTop: 32, textAlign: "center" }}>
            <Btn onClick={onStart} data-testid="footer-start-btn">start the quiz</Btn>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ────────── quiz view ────────── */
function QuizView({ question, index, total, onPick, onBack }) {
  return (
    <section style={{ ...sectionStyle(80, 80), paddingTop: 100, minHeight: "100vh" }}>
      <div style={{ maxWidth: 640, margin: "0 auto" }}>
        {index === 0 && (
          <div style={{ textAlign: "center", marginBottom: 32 }}>
            <h2 style={{ ...h2Style, fontSize: 32, marginBottom: 16 }}>who's driving your desire?</h2>
            <p style={{ ...bodyText, fontSize: 15, fontStyle: "italic", color: "rgba(245,240,232,0.65)" }}>
              eight questions. answer like your mama isn't reading over your shoulder.
            </p>
          </div>
        )}
        <div style={{ fontSize: 11, letterSpacing: 3, textTransform: "uppercase", color: COLORS.gold, marginBottom: 20 }}>
          {index + 1} / {total}
        </div>
        <h3 style={{ fontFamily: "Georgia, serif", fontSize: 26, lineHeight: 1.25, color: COLORS.cream, marginBottom: 32, fontWeight: 400 }} data-testid="quiz-question">
          {question.q}
        </h3>
        <div style={{ display: "grid", gap: 14 }}>
          {question.a.map((opt) => (
            <button
              key={opt.k}
              onClick={() => onPick(opt.k)}
              data-testid={`quiz-answer-${index}-${opt.k}`}
              style={{
                background: "transparent",
                color: COLORS.cream,
                border: `1px solid ${COLORS.gold}`,
                padding: "18px 20px",
                fontSize: 15,
                fontFamily: "Georgia, serif",
                textAlign: "left",
                cursor: "pointer",
                borderRadius: 2,
                lineHeight: 1.5,
                transition: "background 0.2s ease, transform 0.15s ease",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = "rgba(201,168,76,0.1)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = "transparent";
              }}
            >
              {opt.t}
            </button>
          ))}
        </div>
        <div style={{ marginTop: 32, textAlign: "center" }}>
          <button onClick={onBack} data-testid="quiz-back" style={{
            background: "transparent", border: "none", color: "rgba(245,240,232,0.5)",
            fontSize: 13, fontFamily: "inherit", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 4,
          }}>
            ← back
          </button>
        </div>
      </div>
    </section>
  );
}

/* ────────── email view ────────── */
function EmailView({ email, setEmail, website, setWebsite, onSubmit, submitting, onBack }) {
  return (
    <section style={{ ...sectionStyle(100, 80), paddingTop: 120, minHeight: "100vh" }}>
      <div style={{ maxWidth: 540, margin: "0 auto", textAlign: "center" }}>
        <Eyebrow>almost there</Eyebrow>
        <h2 style={{ ...h2Style, fontSize: 30, marginBottom: 20 }}>your result is ready, and honey, she has a lot to say.</h2>
        <p style={{ ...bodyText, marginBottom: 36 }}>
          where should we send it? you'll also get the respectability bargain, the free chapter of showgirl desire, straight to your inbox.
        </p>
        <form onSubmit={onSubmit} style={{ display: "grid", gap: 16 }}>
          {/* honeypot — must stay empty. hidden from humans, visible to bots. */}
          <input
            type="text"
            name="website"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            autoComplete="off"
            tabIndex={-1}
            aria-hidden="true"
            style={{ position: "absolute", left: "-10000px", width: 1, height: 1, opacity: 0 }}
          />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your email"
            required
            data-testid="quiz-email-input"
            style={{
              background: "transparent",
              color: COLORS.cream,
              border: `1px solid ${COLORS.gold}`,
              padding: "16px 18px",
              fontSize: 16,
              fontFamily: "Georgia, serif",
              borderRadius: 2,
              textAlign: "center",
            }}
          />
          <Btn disabled={submitting} data-testid="quiz-email-submit">
            {submitting ? "sending…" : "show me who's driving"}
          </Btn>
        </form>
        <div style={{ marginTop: 24 }}>
          <button onClick={onBack} data-testid="quiz-email-back" style={{
            background: "transparent", border: "none", color: "rgba(245,240,232,0.5)",
            fontSize: 13, fontFamily: "inherit", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 4,
          }}>
            ← back to the quiz
          </button>
        </div>
      </div>
    </section>
  );
}

/* ────────── result view ────────── */
function ResultView({ result, chapterUrl, onShare, onRetake }) {
  return (
    <section style={{ ...sectionStyle(100, 100), paddingTop: 100 }}>
      <div style={{ maxWidth: 680, margin: "0 auto" }}>
        <Eyebrow>your result</Eyebrow>
        <h1 style={{ ...heroH1, fontSize: 46, marginBottom: 32 }} data-testid="quiz-result-name">
          {result.name}.
        </h1>
        {result.description.map((p, i) => (
          <p key={i} style={{ ...bodyText, marginBottom: 20 }}>{p}</p>
        ))}

        <div style={{
          marginTop: 32,
          padding: "24px 24px 24px 28px",
          background: "rgba(201,168,76,0.07)",
          borderLeft: `3px solid ${COLORS.gold}`,
          borderRadius: 2,
        }} data-testid="quiz-try-tonight">
          <div style={{ fontSize: 11, letterSpacing: 3, textTransform: "uppercase", color: COLORS.gold, marginBottom: 10 }}>
            try this tonight
          </div>
          <p style={{ ...bodyText, margin: 0, fontSize: 15 }}>{result.try_tonight}</p>
        </div>

        {/* closing block — same on every result */}
        <div style={{ marginTop: 48, paddingTop: 32, borderTop: `1px solid rgba(201,168,76,0.3)` }}>
          <p style={{ ...bodyText, marginBottom: 24 }}>
            your free chapter, the respectability bargain, is on its way to your inbox. it names the deal you made with your desire, and who set the price. pour something delicious and give it twenty minutes.
          </p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <a href={chapterUrl} data-testid="quiz-read-chapter"
              style={{
                background: COLORS.gold, color: COLORS.bg, textDecoration: "none",
                padding: "16px 28px", fontSize: 14, letterSpacing: 1.5, textTransform: "lowercase",
                borderRadius: 2, display: "inline-block", fontFamily: "'Inter', sans-serif",
              }}>
              read the free chapter now →
            </a>
            <button onClick={onShare} data-testid="quiz-share-btn"
              style={{
                background: "transparent", color: COLORS.cream, border: `1px solid ${COLORS.gold}`,
                padding: "16px 28px", fontSize: 14, letterSpacing: 1.5, textTransform: "lowercase",
                borderRadius: 2, cursor: "pointer", fontFamily: "'Inter', sans-serif",
              }}>
              share my result
            </button>
          </div>
          <p style={{ fontSize: 12, color: "rgba(245,240,232,0.5)", fontStyle: "italic", marginTop: 32 }}>
            the characters in this quiz are based on neuroanatomist jill bolte taylor's book whole brain living (2021).
          </p>
          <div style={{ marginTop: 32 }}>
            <button onClick={onRetake} data-testid="quiz-retake"
              style={{
                background: "transparent", border: "none", color: "rgba(245,240,232,0.5)",
                fontSize: 13, fontFamily: "inherit", cursor: "pointer",
                textDecoration: "underline", textUnderlineOffset: 4,
              }}>
              take it again
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ────────── shared styles ────────── */
const sectionStyle = (top, bottom) => ({ padding: `${top}px 24px ${bottom}px`, position: "relative" });
const heroH1 = { fontFamily: "Georgia, serif", fontSize: "clamp(38px, 7vw, 60px)", lineHeight: 1.1, color: COLORS.cream, fontWeight: 400, letterSpacing: "-0.5px", margin: 0 };
const h2Style = { fontFamily: "Georgia, serif", fontSize: "clamp(26px, 4vw, 36px)", lineHeight: 1.2, color: COLORS.cream, fontWeight: 400, margin: "0 0 24px 0" };
const bodyText = { fontFamily: "Georgia, serif", fontSize: 17, lineHeight: 1.7, color: "rgba(245,240,232,0.82)", margin: 0 };
const ulStyle = { listStyle: "none", padding: 0, margin: 0 };
const liStyle = { ...bodyText, paddingLeft: 24, position: "relative", marginBottom: 18 };
const strongStyle = { color: COLORS.cream, fontWeight: 600 };
const cardStyle = { padding: "28px 28px", background: "rgba(201,168,76,0.04)", border: "1px solid rgba(201,168,76,0.25)", borderRadius: 2 };
